import { Feature } from '@fytness/shared';
import { startOfDay, subDays } from 'date-fns';
import { useMemo } from 'react';
import { useHasFeature } from '~/purchases/hooks/useHasFeature';

/**
 * Free-tier history window in days. Users without {@link Feature.UNLIMITED_HISTORY} can
 * only browse the last 30 days of workout/meal history.
 *
 * NOTE: this is a UI-only bound. PowerSync sync rules cannot express a per-user rolling
 * time window (range filters aren't supported), so the window is enforced here (and in the
 * history read queries) rather than at the sync layer — all history still syncs to the
 * device.
 */
export const FREE_HISTORY_WINDOW_DAYS = 30;

/**
 * The earliest date the current user may navigate to in history views, or `null` when
 * unrestricted (entitled tiers). Free users are clamped to the start of the day
 * {@link FREE_HISTORY_WINDOW_DAYS} days ago.
 */
export const useHistoryMinDate = (): Date | null => {
  const hasUnlimitedHistory = useHasFeature(Feature.UNLIMITED_HISTORY);
  // Stable reference until entitlement changes; the day-boundary drift within a
  // session is irrelevant to a 30-day window.
  return useMemo(
    () =>
      hasUnlimitedHistory
        ? null
        : startOfDay(subDays(new Date(), FREE_HISTORY_WINDOW_DAYS)),
    [hasUnlimitedHistory],
  );
};
